import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ExternalLink, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { API } from '../App';
import { Button } from './ui/button';
import { devError } from '../utils/logger';

const FIELDS = [
  { key: 'logo_url', label: 'Logo URL', placeholder: 'https://...', type: 'url' },
  { key: 'banner_url', label: 'Banner image URL', placeholder: 'https://... (1920×400 recommended)', type: 'url' },
  { key: 'tagline', label: 'Tagline', placeholder: 'One line that describes your business', type: 'text', maxLength: 120 },
  { key: 'public_phone', label: 'Public phone', placeholder: '+61 ...', type: 'tel' },
  { key: 'public_email', label: 'Public email', placeholder: 'sales@yourcompany.com', type: 'email' },
  { key: 'public_address', label: 'Public address', placeholder: '123 Salvage Way, Melbourne', type: 'text' },
];

/**
 * Storefront editor lives inside the SupplierDashboard.
 * Lets a supplier configure their public `/s/{slug}` (and `{slug}.patinamart.com`) page.
 */
export default function StorefrontEditor() {
  const [storefront, setStorefront] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null); // { available, reason }
  const [slugChecking, setSlugChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/storefront`);
        if (!cancelled) setStorefront(res.data || {});
      } catch (e) {
        devError('storefront load failed', e);
        if (!cancelled) setStorefront({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (key, value) => setStorefront((s) => ({ ...(s || {}), [key]: value }));

  const checkSlug = async () => {
    if (!storefront?.slug) return;
    setSlugChecking(true);
    try {
      const res = await axios.get(`${API}/storefront/check-slug`, { params: { slug: storefront.slug } });
      setSlugStatus(res.data);
      if (res.data.slug && res.data.slug !== storefront.slug) {
        update('slug', res.data.slug);
      }
    } catch (e) {
      setSlugStatus({ available: false, reason: e.response?.data?.detail || 'Could not check' });
    } finally {
      setSlugChecking(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/storefront`, storefront);
      setStorefront(res.data);
      toast.success('Storefront saved');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const slug = storefront?.slug || '';

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-playfair text-2xl mb-1">Your Storefront</h2>
        <p className="text-sm text-muted-foreground">
          Configure a public branded page for your business. Once you've claimed
          your slug, your storefront is reachable at:
        </p>
        <code className="inline-block mt-2 px-3 py-1.5 rounded bg-secondary/40 text-accent text-sm" data-testid="storefront-public-url">
          {slug ? `${window.location.origin}/s/${slug}` : '—'}
        </code>
        {slug && (
          <a
            href={`/s/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 ml-3 text-sm text-accent hover:underline"
            data-testid="storefront-preview-link"
          >
            Preview <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Slug */}
      <div className="space-y-2">
        <label className="text-sm font-medium block">Storefront URL slug</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={slug}
            onChange={(e) => { update('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setSlugStatus(null); }}
            placeholder="adl-salvage"
            maxLength={40}
            className="flex-1 h-10 px-3 bg-secondary/30 border border-border/40 rounded-lg text-sm"
            data-testid="storefront-slug-input"
          />
          <Button onClick={checkSlug} disabled={!slug || slugChecking} variant="outline" size="sm">
            {slugChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
          </Button>
        </div>
        {slugStatus && (
          <p className={`text-xs flex items-center gap-1 ${slugStatus.available ? 'text-green-500' : 'text-destructive'}`} data-testid="storefront-slug-status">
            {slugStatus.available ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {slugStatus.available ? 'Available' : slugStatus.reason}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          1-40 lowercase letters, numbers, hyphens. Choose carefully — this is the URL your clients will use.
        </p>
      </div>

      {/* Accent color */}
      <div className="space-y-2">
        <label className="text-sm font-medium block">Accent color</label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={storefront?.accent_color || '#c9a961'}
            onChange={(e) => update('accent_color', e.target.value)}
            className="w-14 h-10 rounded-lg border border-border/40 bg-transparent cursor-pointer"
            data-testid="storefront-accent-input"
          />
          <input
            type="text"
            value={storefront?.accent_color || '#c9a961'}
            onChange={(e) => update('accent_color', e.target.value)}
            className="w-32 h-10 px-3 bg-secondary/30 border border-border/40 rounded-lg text-sm font-mono"
            placeholder="#c9a961"
          />
          <span className="text-xs text-muted-foreground">Used for headings and hero band</span>
        </div>
      </div>

      {/* Simple text fields */}
      {FIELDS.map((f) => (
        <div className="space-y-2" key={f.key}>
          <label className="text-sm font-medium block">{f.label}</label>
          <input
            type={f.type}
            value={storefront?.[f.key] || ''}
            onChange={(e) => update(f.key, e.target.value)}
            placeholder={f.placeholder}
            maxLength={f.maxLength}
            className="w-full h-10 px-3 bg-secondary/30 border border-border/40 rounded-lg text-sm"
            data-testid={`storefront-${f.key}-input`}
          />
        </div>
      ))}

      {/* About */}
      <div className="space-y-2">
        <label className="text-sm font-medium block">About</label>
        <textarea
          value={storefront?.about || ''}
          onChange={(e) => update('about', e.target.value)}
          placeholder="Tell visitors about your business — history, specialties, what makes you different."
          rows={6}
          maxLength={2000}
          className="w-full p-3 bg-secondary/30 border border-border/40 rounded-lg text-sm resize-none"
          data-testid="storefront-about-input"
        />
      </div>

      {/* Visibility toggle */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={storefront?.enabled !== false}
          onChange={(e) => update('enabled', e.target.checked)}
          className="w-4 h-4 rounded border-border/40 text-accent"
          data-testid="storefront-enabled-checkbox"
        />
        <span className="text-sm">Storefront is publicly visible</span>
      </label>

      <div className="flex gap-3 pt-4 border-t border-border/30">
        <Button onClick={save} disabled={saving} className="btn-gold" data-testid="storefront-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Storefront
        </Button>
      </div>
    </div>
  );
}
